import { fetchMemberCsrf, getMemberCsrfToken } from '@/services/memberAuth';

export type MemberCustomerServiceSourceType = 'Manual' | 'ProductTrial' | 'Reservation' | 'Order' | 'Member';
export type MemberCustomerServiceStatus = 'New' | 'InProgress' | 'WaitingCustomer' | 'WaitingInternal' | 'Resolved' | 'Closed';
export type MemberCustomerServiceCategory = 'General' | 'Product' | 'Reservation' | 'Order' | 'Member' | 'AfterSales';

export type MemberCustomerServiceTicket = {
  TicketId: string;
  TicketNumber: string;
  SourceType: MemberCustomerServiceSourceType;
  SourceId: string | null;
  Subject: string;
  Category: MemberCustomerServiceCategory;
  Priority: string;
  Status: MemberCustomerServiceStatus;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type MemberCustomerServiceActivity = {
  ActivityId: string;
  ActivityType: string;
  Body: string;
  AuthorName: string;
  CreatedAt: string | null;
};

type ApiResponse<T> = T & { ok: boolean; message?: string; errors?: Record<string, string> };

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || '客服資料處理失敗，請稍後再試。');
  }
  return data as T;
}

async function ensureCsrf() {
  if (!getMemberCsrfToken()) {
    await fetchMemberCsrf();
  }
}

export async function listMemberCustomerServiceTickets(SourceType?: MemberCustomerServiceSourceType, SourceId?: string) {
  const query = new URLSearchParams();
  if (SourceType) query.set('SourceType', SourceType);
  if (SourceId) query.set('SourceId', SourceId);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await fetch(`/api/member/customer-service/tickets${suffix}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then((response) => parseJsonResponse<{ ok: boolean; Tickets: MemberCustomerServiceTicket[] }>(response));
  return data.Tickets;
}

export async function createMemberCustomerServiceTicket(payload: {
  SourceType: MemberCustomerServiceSourceType;
  SourceId?: string | null;
  Subject: string;
  Category: MemberCustomerServiceCategory;
  Message: string;
}) {
  await ensureCsrf();
  const data = await fetch('/api/member/customer-service/tickets', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': getMemberCsrfToken(),
    },
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Ticket: MemberCustomerServiceTicket }>(response));
  return data.Ticket;
}

export async function getMemberCustomerServiceTicket(ticketId: string) {
  const data = await fetch(`/api/member/customer-service/tickets/${encodeURIComponent(ticketId)}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then((response) => parseJsonResponse<{ ok: boolean; Ticket: MemberCustomerServiceTicket; Activities: MemberCustomerServiceActivity[] }>(response));
  return data;
}

export const memberCustomerServiceStatusLabels: Record<MemberCustomerServiceStatus, string> = {
  New: '已送出',
  InProgress: '處理中',
  WaitingCustomer: '等待回覆',
  WaitingInternal: '內部確認',
  Resolved: '已解決',
  Closed: '已結案',
};

export const memberCustomerServiceStatusClass: Record<MemberCustomerServiceStatus, string> = {
  New: 'bg-[#e9f4ec] text-[#245438]',
  InProgress: 'bg-[#eef3f8] text-[#255071]',
  WaitingCustomer: 'bg-[#fff6df] text-[#7a5610]',
  WaitingInternal: 'bg-[#f3ecdf] text-[#7a5610]',
  Resolved: 'bg-[#e7f7f2] text-[#16705a]',
  Closed: 'bg-[#eeeeee] text-[#59635d]',
};
